import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import supabase from '../utils/supabaseUtils';
import stackedLogo from '../assets/playmaker-logo-stacked-w-tag.png';
import './AppNavigation.css';
import { trackEvent } from '../utils/analyticsUtils';
import { MdAutoAwesome, MdScience } from 'react-icons/md';

const AppNavigation = ({ user, isEditor = false, onSectionChange }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarMinimized, setSidebarMinimized] = useState(false);
  const [activeSection, setActiveSection] = useState('designs');
  const [isAdmin, setIsAdmin] = useState(false);
  
  const location = useLocation();
  const navigate = useNavigate();

  // Check if current route is editor
  useEffect(() => {
    if (location.pathname.includes('/editor')) {
      document.body.setAttribute('data-page', 'editor');
      setActiveSection('editor');
    } else {
      document.body.setAttribute('data-page', 'dashboard');
      // Set active section based on path
      const path = location.pathname.split('/')[1] || 'dashboard';
      
      // When path is 'dashboard', set the active section to 'designs'
      if (path === 'dashboard') {
        setActiveSection('designs');
        // Notify parent of the section change
        if (onSectionChange) {
          onSectionChange('designs');
        }
      } else {
        setActiveSection(path);
      }
    }
    
    return () => {
      document.body.removeAttribute('data-page');
    };
  }, [location, onSectionChange]);

  // Check if user is admin based on app_users table and respect impersonation state
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) {
        setIsAdmin(false);
        return;
      }

      try {
        // Check for impersonation state
        const params = new URLSearchParams(window.location.search);
        const isImpersonating = params.get('impersonating') === 'true';

        // If impersonating, force admin to false
        if (isImpersonating) {
          console.log('User is impersonating, forcing admin to false');
          setIsAdmin(false);
          return;
        }

        const { data, error } = await supabase
          .from('app_users')
          .select('user_type, is_admin')
          .eq('id', user.id)
          .single();

        if (error) {
          console.warn('Admin check failed:', error.message);
          setIsAdmin(false);
          return;
        }

        const isAdminUser = data?.user_type === 'staff' && data?.is_admin === true;
        console.log('Setting admin status:', { isAdminUser, userData: data });
        setIsAdmin(isAdminUser);
      } catch (err) {
        console.warn('Admin check exception:', err);
        setIsAdmin(false);
      }
    };

    checkAdminStatus();
  }, [user, location.search]); // Also depend on location.search to catch impersonation changes

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    const newMobileMenuState = !mobileMenuOpen;
    setMobileMenuOpen(newMobileMenuState);
    
    // Control body scroll behavior
    if (newMobileMenuState) {
      document.body.classList.add('menu-open');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.classList.remove('menu-open');
      document.body.style.overflow = '';
    }
    
    // Control sidebar visibility
    const sidebarElement = document.querySelector('.app-sidebar');
    if (sidebarElement) {
      if (newMobileMenuState) {
        sidebarElement.classList.add('mobile-open');
      } else {
        sidebarElement.classList.remove('mobile-open');
      }
    }
  };
  
  // Toggle sidebar minimized state
  const toggleSidebar = () => {
    setSidebarMinimized(!sidebarMinimized);
  };
  
  // Select a section and navigate to the corresponding route
  const selectSection = (section) => {
    // Track navigation click – include user id if available
    trackEvent('nav_section_click', {
      section,
      userId: user?.id || null,
    });

    const actualSection = section === 'dashboard' ? 'designs' : section;
    setActiveSection(actualSection);
    setMobileMenuOpen(false);
    
    // Navigate to the corresponding route
    navigate(`/${section}`);
    
    // Call the callback if provided
    if (onSectionChange) {
      onSectionChange(section);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  // If editor mode, hide the sidebar or show a minimal version
  if (isEditor) {
    return (
      <>
        {/* Mobile Header for Editor */}
        <div className="mobile-header">
          <Link to="/dashboard" className="mobile-menu-toggle">
            <i className="fa fa-arrow-left"></i>
          </Link>
          
          <div className="mobile-logo">
            <img src={stackedLogo} alt="PlayMaker Pics" />
          </div>
          
          <div className="mobile-user">
            {user && (
              <div className="avatar">
                {user?.user_metadata?.avatar_url ? (
                  <img src={user.user_metadata.avatar_url} alt="User avatar" />
                ) : (
                  <div className="avatar-placeholder">
                    {(user?.user_metadata?.given_name?.[0] || user?.email?.[0] || 'U').toUpperCase()}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Mobile Header */}
      <div className="mobile-header">
        <button 
          className="mobile-menu-toggle" 
          onClick={toggleMobileMenu}
          aria-label="Toggle menu"
        >
          <i className={`fa ${mobileMenuOpen ? 'fa-times' : 'fa-bars'}`}></i>
        </button>
        
        <div className="mobile-logo">
          <img src={stackedLogo} alt="PlayMaker Pics" />
        </div>
        
        <div className="mobile-user">
          {user && (
            <div className="avatar">
              {user?.user_metadata?.avatar_url ? (
                <img src={user.user_metadata.avatar_url} alt="User avatar" />
              ) : (
                <div className="avatar-placeholder">
                  {(user?.user_metadata?.given_name?.[0] || user?.email?.[0] || 'U').toUpperCase()}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Sidebar */}
      <div className={`app-sidebar ${sidebarMinimized ? 'minimized' : ''} ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <img src={stackedLogo} alt="PlayMaker Pics" className="logo" />
          <button className="minimize-button" onClick={toggleSidebar}>
            {sidebarMinimized ? '»' : '«'}
          </button>
        </div>
        <nav className="sidebar-nav">
          <ul>
            {/* Temporarily hiding Dashboard option since it's confusing */}
            {/* <li className={activeSection === 'dashboard' ? 'active' : ''}>
              <Link to="/dashboard" onClick={() => selectSection('dashboard')}>
                <i className="fa fa-tachometer-alt"></i>
                <span>Dashboard</span>
              </Link>
            </li> */}
            <li className={activeSection === 'designs' ? 'active' : ''}>
              <Link to="/designs" onClick={() => selectSection('designs')}>
                <i className="fa fa-palette"></i>
                <span>Pro Edits</span>
              </Link>
            </li>
            <li className={activeSection === 'templates' ? 'active' : ''}>
              <Link to="/templates" onClick={() => selectSection('templates')}>
                <i className="fa fa-robot"></i>
                <span>AI Designs</span>
                <span className="nav-badge">New</span>
              </Link>
            </li>
            <li className={activeSection === 'images' ? 'active' : ''}>
              <Link to="/images" onClick={() => selectSection('images')}>
                <i className="fa fa-image"></i>
                <span>Media Day</span>
              </Link>
            </li>
            {isAdmin && (
              <li className={activeSection === 'editor' ? 'active' : ''}>
                <Link to="/editor" onClick={() => selectSection('editor')}>
                  <i className="fa fa-edit"></i>
                  <span>Editor</span>
                </Link>
              </li>
            )}
            <li className={activeSection === 'orders' ? 'active' : ''}>
              <Link to="/orders" onClick={() => selectSection('orders')}>
                <i className="fa fa-shopping-bag"></i>
                <span>My Orders</span>
              </Link>
            </li>
            <li className={activeSection === 'packages' ? 'active' : ''}>
              <Link to="/packages" onClick={() => selectSection('packages')}>
                <i className="fa fa-box-open"></i>
                <span>Packages</span>
              </Link>
            </li>
            {isAdmin && (
              <li className={activeSection === 'admin' ? 'active' : ''}>
                <Link to="/admin" onClick={() => selectSection('admin')}>
                  <i className="fa fa-user-shield"></i>
                  <span>Admin</span>
                </Link>
              </li>
            )}
            <li className={activeSection === 'settings' ? 'active' : ''}>
              <Link to="/settings" onClick={() => selectSection('settings')}>
                <i className="fa fa-cog"></i>
                <span>Settings</span>
              </Link>
            </li>
            {/* NEW: Dynamic Generation Test Link */}
            {isAdmin && (
              <li className={activeSection === 'test-dynamic-generation' ? 'active' : ''}>
                <Link to="/test-dynamic-generation" onClick={() => selectSection('test-dynamic-generation')}>
                  <i className="fa fa-flask"></i>
                  <span>🧪 Dynamic Demo</span>
                </Link>
              </li>
            )}
          </ul>
        </nav>
        
        <div className="sidebar-footer">
          <button className="logout-button" onClick={handleLogout}>
            <i className="fa fa-sign-out-alt"></i>
            <span>Log Out</span>
          </button>
        </div>
      </div>
      
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="mobile-menu-overlay active" 
          onClick={toggleMobileMenu}
          aria-label="Close menu"
        ></div>
      )}
    </>
  );
};

export default AppNavigation; 